import React, { useEffect, useMemo, useState } from 'react';

import Button from '@uikit/Form/Button/Button';
import Select from '@uikit/Form/Select/Select';
import { countries } from '@constants/countries.contstants';
import BillingFormField from '@components/Publisher/Settings/SettingsBillingDetails/_components/BillingFormField/BillingFormField';
import {
  isValidEmailField,
  PAYOUT_MAX,
} from '@components/Publisher/Settings/SettingsBillingDetails/_components/payout-validation';
import {
  countryCurrencyMap,
  currencyOptions,
} from '@components/Publisher/Settings/SettingsBillingDetails/_components/BankAccountForm/constants';
import { PayoutFieldErrorType } from '@ts/billing-publisher.types';

import './PayPalAccountForm.scss';

export type PayPalAccountFormValue = {
  email: string;
  transferMethodCountry: string;
  transferMethodCurrency: string;
};

type Props = {
  initialValue?: Partial<PayPalAccountFormValue>;
  defaultCountry?: string;
  onSubmit: (value: PayPalAccountFormValue) => Promise<void> | void;
  onCancel: () => void;
  isSubmitting: boolean;
  isEditing: boolean;
  apiError?: string;
  apiFieldErrors?: PayoutFieldErrorType[];
};

// Proto/snake_case keys the server uses when surfacing per-field errors.
const API_FIELD_EMAIL = 'email';
const API_FIELD_COUNTRY = 'transfer_method_country';
const API_FIELD_CURRENCY = 'transfer_method_currency';

const emptyValue = (): PayPalAccountFormValue => ({
  email: '',
  transferMethodCountry: '',
  transferMethodCurrency: 'USD',
});

function PayPalAccountForm({
  initialValue,
  defaultCountry,
  onSubmit,
  onCancel,
  isSubmitting,
  isEditing,
  apiError,
  apiFieldErrors,
}: Props): JSX.Element {
  const [form, setForm] = useState<PayPalAccountFormValue>(() => {
    const base = { ...emptyValue(), ...(initialValue ?? {}) };
    const initialCountry = (
      initialValue?.transferMethodCountry ||
      defaultCountry ||
      ''
    ).toUpperCase();
    return {
      ...base,
      transferMethodCountry: initialCountry,
      transferMethodCurrency:
        initialValue?.transferMethodCurrency || countryCurrencyMap[initialCountry] || 'USD',
    };
  });

  const [showErrors, setShowErrors] = useState(false);
  const [touched, setTouched] = useState<Set<string>>(new Set());
  // API errors are sticky until the user changes the offending field; clear the
  // dismissal set whenever a fresh `apiFieldErrors` prop arrives so a new error
  // response is shown again. Matches the pattern used by Wise/Bank forms.
  const [dismissedApiFieldKeys, setDismissedApiFieldKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    setDismissedApiFieldKeys(new Set());
  }, [apiFieldErrors]);

  const markTouched = (field: string) => {
    setTouched((prev) => {
      if (prev.has(field)) return prev;
      const next = new Set(prev);
      next.add(field);
      return next;
    });
  };

  const dismissApiFieldError = (apiKey: string) => {
    setDismissedApiFieldKeys((prev) => {
      if (prev.has(apiKey)) return prev;
      const next = new Set(prev);
      next.add(apiKey);
      return next;
    });
  };

  const apiErrorByField = useMemo(() => {
    const map: Record<string, string> = {};
    apiFieldErrors?.forEach(({ field, message }) => {
      if (field && message) map[field] = message;
    });
    return map;
  }, [apiFieldErrors]);

  const apiErrorFor = (apiKey: string): string | undefined =>
    dismissedApiFieldKeys.has(apiKey) ? undefined : apiErrorByField[apiKey];

  const supportedCountries = useMemo(
    () => countries.filter((c) => !!c.value && !!countryCurrencyMap[c.value.toUpperCase()]),
    []
  );

  const fieldErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    if (!isValidEmailField(form.email)) {
      errors.email = 'Enter a valid PayPal email address (max 200 characters).';
    }
    if (!form.transferMethodCountry?.trim()) {
      errors.transferMethodCountry = 'Select a PayPal account country.';
    }
    if (!form.transferMethodCurrency?.trim()) {
      errors.transferMethodCurrency = 'Select a currency.';
    }
    return errors;
  }, [form]);

  const errorFor = (field: string, apiKey?: string): string | undefined => {
    const apiErr = apiKey ? apiErrorFor(apiKey) : undefined;
    if (apiErr) return apiErr;
    if (!showErrors && !touched.has(field)) return undefined;
    return fieldErrors[field];
  };

  const isFormValid = Object.keys(fieldErrors).length === 0;

  const handleCountryChange = (countryCode: string) => {
    const upper = countryCode ? countryCode.toUpperCase() : '';
    const mappedCurrency = countryCurrencyMap[upper];
    setForm((prev) => ({
      ...prev,
      transferMethodCountry: upper,
      transferMethodCurrency: mappedCurrency || prev.transferMethodCurrency || 'USD',
    }));
    markTouched('transferMethodCountry');
    if (apiErrorByField[API_FIELD_COUNTRY]) dismissApiFieldError(API_FIELD_COUNTRY);
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!isFormValid) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    await onSubmit({
      ...form,
      email: form.email.trim(),
      transferMethodCountry: form.transferMethodCountry.toUpperCase(),
    });
  };

  return (
    <form
      className="paypal-account-form"
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      noValidate
    >
      <p className="paypal-account-form__description">
        Enter the email address associated with your PayPal account along with the country and
        currency this account will receive payouts in.
      </p>

      <BillingFormField
        id="paypal-email"
        label="PayPal email address"
        placeholder="name@example.com"
        value={form.email}
        onChange={(v) => {
          setForm((prev) => ({ ...prev, email: v }));
          if (apiErrorByField[API_FIELD_EMAIL]) dismissApiFieldError(API_FIELD_EMAIL);
        }}
        onBlur={() => markTouched('email')}
        isError={!!errorFor('email', API_FIELD_EMAIL)}
        errorMessage={errorFor('email', API_FIELD_EMAIL)}
        maxLength={PAYOUT_MAX.email}
        testId="publisher-billing-paypal-email"
      />

      {/*<div className="paypal-account-form__row">*/}
      {/*  <BillingFormField*/}
      {/*    label="PayPal account country"*/}
      {/*    isError={!!errorFor('transferMethodCountry', API_FIELD_COUNTRY)}*/}
      {/*    errorMessage={errorFor('transferMethodCountry', API_FIELD_COUNTRY)}*/}
      {/*  >*/}
      {/*    <Select*/}
      {/*      options={supportedCountries}*/}
      {/*      value={*/}
      {/*        supportedCountries.find(*/}
      {/*          (c) => c.value.toLowerCase() === form.transferMethodCountry?.toLowerCase()*/}
      {/*        ) || null*/}
      {/*      }*/}
      {/*      onChange={(val: { value: string; label: string } | null) =>*/}
      {/*        handleCountryChange(val?.value || '')*/}
      {/*      }*/}
      {/*      placeholder="Select country"*/}
      {/*      menuPlacement="auto"*/}
      {/*    />*/}
      {/*  </BillingFormField>*/}
      {/*  <BillingFormField*/}
      {/*    label="Currency"*/}
      {/*    isError={!!errorFor('transferMethodCurrency', API_FIELD_CURRENCY)}*/}
      {/*    errorMessage={errorFor('transferMethodCurrency', API_FIELD_CURRENCY)}*/}
      {/*  >*/}
      {/*    <Select*/}
      {/*      options={currencyOptions}*/}
      {/*      value={currencyOptions.find((o) => o.value === form.transferMethodCurrency) || null}*/}
      {/*      onChange={(val: { value: string; label: string } | null) => {*/}
      {/*        setForm((prev) => ({ ...prev, transferMethodCurrency: val?.value || '' }));*/}
      {/*        markTouched('transferMethodCurrency');*/}
      {/*        if (apiErrorByField[API_FIELD_CURRENCY]) dismissApiFieldError(API_FIELD_CURRENCY);*/}
      {/*      }}*/}
      {/*      placeholder="Select currency"*/}
      {/*      menuPlacement="auto"*/}
      {/*    />*/}
      {/*  </BillingFormField>*/}
      {/*</div>*/}

      {!!apiError && (
        <div
          className="paypal-account-form__api-error"
          role="alert"
          data-testid="publisher-billing-paypal-api-error"
        >
          {apiError}
        </div>
      )}

      <div className="paypal-account-form__footer">
        <Button
          className="paypal-account-form__submit"
          size="l"
          type="primary"
          onClick={handleSubmit}
          disabled={isSubmitting}
          isLoading={isSubmitting}
          data-testid="publisher-billing-paypal-submit"
        >
          {isEditing ? 'Update PayPal account' : 'Connect PayPal account'}
        </Button>
        <Button
          type="ghost"
          onClick={onCancel}
          disabled={isSubmitting}
          data-testid="publisher-billing-paypal-cancel"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

export default PayPalAccountForm;
